/**
 * Molecule ED Assessment — LoopRX create-ed-assessment API
 */
(function (global) {
  'use strict';

  function getCookie(name) {
    var m = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
    );
    return m ? decodeURIComponent(m[1]) : '';
  }

  function asStringValue(list) {
    if (list == null || list === '') return '';
    if (!Array.isArray(list)) return String(list);
    if (!list.length) return '';
    if (list.length === 1) return list[0];
    return list.join(', ');
  }

  /**
   * UI-only confirmation keys (never sent in assessment JSON).
   * Includes legacy names and current `_ui_*` storage keys.
   */
  var OMIT_API_KEYS = {
    smoke_alcohol_ack: true,
    confirm_understand: true,
    agree_terms: true,
    as_needed_claims_ack: true,
    _ui_smoke_alcohol_ack: true,
    _ui_confirm_understand: true,
    _ui_agree_terms: true,
    _ui_as_needed_claims_ack: true
  };

  function shouldOmitApiKey(key) {
    if (!key) return true;
    if (OMIT_API_KEYS[key]) return true;
    if (key.charAt(0) === '_') return true;
    return false;
  }

  /** Map option values → full labels for a phase. */
  function buildLabelMap(phase) {
    var map = {};
    (phase && phase.options ? phase.options : []).forEach(function (opt) {
      if (!opt || opt.value == null) return;
      map[String(opt.value)] = opt.label != null ? String(opt.label) : String(opt.value);
    });
    return map;
  }

  /**
   * Convert stored option value(s) to full answer labels.
   * "other" → "Other - {text}" when free text is present.
   */
  function toAnswerLabels(value, labelMap, otherText, otherValue) {
    var marker = otherValue || 'other';
    var other = (otherText || '').trim();

    function one(v) {
      if (v == null || v === '') return '';
      var key = String(v);
      if (key === marker) {
        var otherLabel = (labelMap && labelMap[marker]) || 'Other';
        return other ? otherLabel + ' - ' + other : otherLabel;
      }
      if (labelMap && labelMap[key] != null) return labelMap[key];
      return key;
    }

    if (Array.isArray(value)) {
      return asStringValue(
        value.map(one).filter(function (s) {
          return !!s;
        })
      );
    }
    if (typeof value === 'boolean') return value;
    return one(value);
  }

  function phaseApiKey(phase) {
    return (phase && (phase.apiKey || phase.field)) || '';
  }

  function phaseOtherStorageKey(phase) {
    if (!phase || !phase.otherValue) return '';
    if (phase.otherApiKey) return phase.otherApiKey;
    var key = phaseApiKey(phase);
    return key ? key + '_other' : '';
  }

  function buildAssessmentPayload(state, config) {
    var a = state.answers || {};
    var api = (config && config.api) || {};
    var payload = {
      form_type: api.formType || config.formType || 'webmed_ed',
      category_id: Number(api.categoryId) || 2
    };

    var phases = (config && config.phases) || [];
    var handled = { form_type: true, category_id: true };

    Object.keys(OMIT_API_KEYS).forEach(function (k) {
      handled[k] = true;
    });

    phases.forEach(function (phase) {
      if (!phase) return;
      var key = phaseApiKey(phase);

      if (shouldOmitApiKey(key)) {
        if (key) handled[key] = true;
        var omitOther = phaseOtherStorageKey(phase);
        if (omitOther) handled[omitOther] = true;
        if (phase.ackApiKey) handled[phase.ackApiKey] = true;
        if (phase.checkboxes) {
          phase.checkboxes.forEach(function (c) {
            var cKey = c.apiKey || c.field;
            if (cKey) handled[cKey] = true;
          });
        }
        return;
      }

      if (key && !handled[key]) {
        var otherKey = phaseOtherStorageKey(phase);
        var labelMap = buildLabelMap(phase);
        if (phase.options && phase.options.length) {
          payload[key] = toAnswerLabels(
            a[key],
            labelMap,
            otherKey ? a[otherKey] : '',
            phase.otherValue
          );
        } else if (Array.isArray(a[key])) {
          payload[key] = asStringValue(a[key]);
        } else if (typeof a[key] === 'boolean') {
          payload[key] = a[key];
        } else {
          payload[key] = a[key] == null ? '' : a[key];
        }
        handled[key] = true;
        if (otherKey) handled[otherKey] = true;
      }

      if (phase.ackApiKey) handled[phase.ackApiKey] = true;
      if (phase.checkboxes) {
        phase.checkboxes.forEach(function (c) {
          var cKey = c.apiKey || c.field;
          if (cKey) handled[cKey] = true;
        });
      }
    });

    Object.keys(a).forEach(function (key) {
      if (handled[key] || shouldOmitApiKey(key)) return;
      if (/_other$/.test(key)) return;
      var val = a[key];
      if (Array.isArray(val)) payload[key] = asStringValue(val);
      else payload[key] = val;
    });

    // Final safety: strip any omitted keys if present
    Object.keys(OMIT_API_KEYS).forEach(function (k) {
      delete payload[k];
    });
    Object.keys(payload).forEach(function (k) {
      if (k.charAt(0) === '_') delete payload[k];
    });

    return payload;
  }

  function buildFormData(state, config) {
    var a = state.answers || {};
    var api = (config && config.api) || {};
    var email = (a.email || '').trim();
    var firstName = (a.first_name || '').trim();
    var lastName = (a.last_name || '').trim();
    var phone = (a.phone || '').trim();
    var fd = new FormData();
    fd.append('patient_email', email);
    // create-ed-assessment validates `first_name`; WL create-assessment uses First-Name
    fd.append('first_name', firstName);
    fd.append('First-Name', firstName);
    fd.append('last_name', lastName);
    fd.append('Last-Name', lastName);
    fd.append('Phone', phone);
    fd.append('category_id', String(Number(api.categoryId) || 2));
    fd.append('assessment', JSON.stringify(buildAssessmentPayload(state, config)));
    return fd;
  }

  function buildSelectProductUrl(config, state) {
    var base = (config && config.successRedirectUrl) || '/pages/select-product';
    var url;
    try {
      url = new URL(base, window.location.origin);
    } catch (e) {
      url = new URL('/pages/select-product', window.location.origin);
    }
    var a = state.answers || {};
    if (a.email) url.searchParams.set('email', a.email);
    if (a.first_name) url.searchParams.set('first_name', a.first_name);
    if (a.last_name) url.searchParams.set('last_name', a.last_name);
    if (a.phone) url.searchParams.set('phone', String(a.phone).trim());
    return url.pathname + url.search;
  }

  /**
   * LoopRX quiz-plp — fetch patient + products for this website's tenant.
   * GET {quizPlpUrl}?email={email}&tenant={tenant}
   * Also sends X-Tenant (same tenant string — not a separate API key).
   */
  function fetchQuizPlp(email, config) {
    var api = (config && config.api) || {};
    var tenant = api.tenant || 'webmed';
    var base =
      api.quizPlpUrl ||
      (config && config.quizPlpUrl) ||
      'https://demo-app-config.looprx.io/api/quiz-plp';
    var url;
    try {
      url = new URL(base, window.location.origin);
    } catch (e) {
      url = new URL('https://demo-app-config.looprx.io/api/quiz-plp');
    }
    if (email) url.searchParams.set('email', email);
    url.searchParams.set('tenant', tenant);

    var headers = {
      Accept: 'application/json',
      'X-Tenant': tenant
    };
    var token = getCookie(api.authCookie || 'access_token');
    if (token) headers.Authorization = 'Bearer ' + token;

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timeoutMs =
      typeof api.quizPlpTimeoutMs === 'number' ? api.quizPlpTimeoutMs : 8000;
    var timer = null;
    if (controller) {
      timer = setTimeout(function () {
        try {
          controller.abort();
        } catch (e) {}
      }, timeoutMs);
    }

    return fetch(url.toString(), {
      method: 'GET',
      headers: headers,
      // Cross-origin quiz-plp: avoid credentialed CORS hangs on preview hosts
      credentials: 'omit',
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        return res.text().then(function (text) {
          var body = {};
          if (text && text.trim()) {
            try {
              body = JSON.parse(text);
            } catch (e) {
              body = { message: text };
            }
          }
          if (!res.ok || body.status === 'error' || body.success === false) {
            var msg =
              (body && body.message) ||
              'Unable to load patient data (' + res.status + ')';
            throw new Error(msg);
          }
          return body;
        });
      })
      .finally(function () {
        if (timer) clearTimeout(timer);
      });
  }

  /** Map quiz-plp `data` onto assessment answers (fills empty fields only). */
  function applyQuizPlpToAnswers(answers, data) {
    if (!answers || !data) return answers;
    if (!(answers.first_name || '').trim() && data.patient_first_name) {
      answers.first_name = String(data.patient_first_name).trim();
    }
    if (!(answers.last_name || '').trim() && data.last_name) {
      answers.last_name = String(data.last_name).trim();
    }
    if (!(answers.phone || '').trim() && data.phone_number) {
      answers.phone = String(data.phone_number).trim();
    }
    return answers;
  }

  function ensureSubmitIdentity(state) {
    var a = (state && state.answers) || {};
    if (!(a.email || '').trim()) {
      throw new Error('Email is required. Please enter your details on the first step.');
    }
    if (!(a.first_name || '').trim()) {
      throw new Error('First name is required. Please enter your details on the first step.');
    }
  }

  function postCreateEdAssessment(state, config) {
    ensureSubmitIdentity(state);
    var api = (config && config.api) || {};
    var url = api.url || 'https://demo-app-config.looprx.io/api/create-ed-assessment';
    var headers = {
      Accept: 'application/json',
      'X-Tenant': api.tenant || 'webmed'
    };
    var token = getCookie(api.authCookie || 'access_token');
    if (token) headers.Authorization = 'Bearer ' + token;

    return fetch(url, {
      method: 'POST',
      headers: headers,
      body: buildFormData(state, config),
      credentials: 'include'
    }).then(function (res) {
      return res.text().then(function (text) {
        var body = {};
        if (text && text.trim()) {
          try {
            body = JSON.parse(text);
          } catch (e) {
            body = { message: text };
          }
        }
        return { ok: res.ok, status: res.status, body: body };
      });
    });
  }

  function formatValidationErrors(data) {
    if (!data || typeof data !== 'object') return '';
    var parts = [];
    Object.keys(data).forEach(function (key) {
      var val = data[key];
      if (Array.isArray(val) && val.length) parts.push(val[0]);
      else if (typeof val === 'string' && val) parts.push(val);
    });
    return parts.join(' ');
  }

  function submitAssessment(state, config) {
    return Promise.resolve()
      .then(function () {
        return postCreateEdAssessment(state, config);
      })
      .then(function (result) {
        var body = (result && result.body) || {};
        var failed =
          !result.ok ||
          body.success === false ||
          body.status === 'error' ||
          (body.error && body.error.success === false);

        if (failed) {
          var msg =
            formatValidationErrors(body.data) ||
            (body.error && body.error.message) ||
            body.message ||
            'Unable to submit assessment. Please try again.';
          throw new Error(msg);
        }

        try {
          localStorage.setItem(
            ((config && config.storageKey) || 'webmed_ed_assessment') + '_result',
            JSON.stringify(body)
          );
        } catch (e) {}

        return { ok: true, body: body };
      });
  }

  global.WmEdAssessmentApi = {
    buildAssessmentPayload: buildAssessmentPayload,
    buildSelectProductUrl: buildSelectProductUrl,
    fetchQuizPlp: fetchQuizPlp,
    applyQuizPlpToAnswers: applyQuizPlpToAnswers,
    submitAssessment: submitAssessment,
    getCookie: getCookie
  };
})(window);
